import { CategoryProductsService } from '@shared/service/homepage-data/category-products.service';
import { Component, ChangeDetectionStrategy, inject, OnDestroy, AfterViewInit, ViewChild, ElementRef, NgZone } from '@angular/core';
import { WindowService } from '@shared/window';
import Swiper from 'swiper';
import { FreeMode, Pagination } from 'swiper/modules';
import { combineLatest, delay, filter, Subscription, take, tap } from 'rxjs';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { RouterLink } from '@angular/router';
import { ProductThumbnailComponent } from '../../../shared/components/product-thumbnail/product-thumbnail.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalizePipe } from '../../../i18n/pipes/lozalize.pipe';

@Component({
    selector: 'app-new-arrivals',
    templateUrl: './new-arrivals.component.html',
    styleUrls: ['./new-arrivals.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RouterLink, ProductThumbnailComponent, AsyncPipe, TranslatePipe, LocalizePipe],
})
export class NewArrivalsComponent implements OnDestroy, AfterViewInit {
    private zone = inject(NgZone);
    private categoryProductsService = inject(CategoryProductsService);
    private windowService = inject(WindowService);

    @ViewChild('swiper') swiperRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperPagination') swiperPaginationRef!: ElementRef<HTMLDivElement>;

    readonly isClient = this.windowService.isClient;

    private swiper?: Swiper;

    readonly category$ = this.categoryProductsService.category$;
    readonly products$ = this.categoryProductsService.productsByCategory$;

    private subscriptions$ = new Subscription();

    ngAfterViewInit() {
        if (this.windowService.isBrowser) {
            this.subscriptions$.add(
                combineLatest([this.category$.pipe(filter((value) => value !== null)), this.products$.pipe(filter((value) => value !== null))])
                    .pipe(
                        take(1),
                        delay(0),
                        tap(() => this.initSwiper()),
                    )
                    .subscribe(),
            );
        }
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
        this.subscriptions$.unsubscribe();
    }

    private initSwiper() {
        this.zone.runOutsideAngular(() => {
            this.swiper = new Swiper(this.swiperRef.nativeElement, {
                modules: [Pagination, FreeMode],
                spaceBetween: 26,
                autoplay: {
                    delay: 4000,
                    disableOnInteraction: false,
                },
                freeMode: {
                    enabled: true,
                    sticky: true,
                    momentum: false,
                },
                speed: 1500,
                loop: true,
                shortSwipes: false,
                pagination: {
                    el: this.swiperPaginationRef.nativeElement,
                    type: 'bullets',
                },
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                breakpoints: {
                    0: {
                        slidesPerView: 'auto',
                    },
                    768: {
                        slidesPerView: 3,
                    },
                },
                threshold: 5,
            });
        });
    }
}
